import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Play, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import { authService } from '../services/authService';

import { getApiBaseUrl } from '../services/api';
import { isValidPhoneNumber, PHONE_NUMBER_ERROR } from '../utils/phoneValidation';

const OutboundLeadModal = ({ isOpen, onClose, onLeadCreated, selectedStore, selectedStoreAssistant }) => {
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fileError, setFileError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [formData, setFormData] = useState({
    phoneNumber: ''
  });
  const [storeWarning, setStoreWarning] = useState('');
  const dataLoadedRef = useRef(false);

  useEffect(() => {
    if (isOpen && !dataLoadedRef.current) {
      setLoadingFiles(true);
      dataLoadedRef.current = true;
      fetchFiles();
    } else if (!isOpen) {
      // Reset the ref when modal closes so data loads again when reopened
      dataLoadedRef.current = false;
      setStoreWarning('');
      setFileError('');
      setFiles([]);
    }
  }, [isOpen]);

  // Separate effect for store warning logic
  useEffect(() => {
    if (isOpen) {
      // Check if selected store has assistant
      if (selectedStore) {
        if (selectedStoreAssistant) {
          // Store has assistant, use it
          setFormData(prev => ({ ...prev, storeId: selectedStore }));
          setStoreWarning('');
        } else {
          // Store has no assistant, show warning
          setStoreWarning('Selected store has no assistant attached. Please select a different store.');
        }
      } else {
        // No store selected, show warning
        setStoreWarning('No store selected. Please select a store from the store selection section first.');
      }
    }
  }, [isOpen, selectedStore, selectedStoreAssistant]);

  const fetchFiles = useCallback(async () => {
    setError('');
    try {
      setFileError('');
      const response = await fetch(`${getApiBaseUrl()}/outbound-leads/available-files`, {
        headers: authService.getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setFiles(data);
      } else {
        setFileError('Failed to fetch available files');
      }
    } catch (err) {
      setFileError('Error fetching available files');
    } finally {
      setLoadingFiles(false);
    }
  }, []);

  const handleRetryFetchFiles = () => {
    setLoadingFiles(true);
    fetchFiles();
  };

  const fetchStores = async () => {
    // No longer needed since we use parent's selected store
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile || !selectedStore) {
      setError('Please select a file and ensure a store is selected');
      return;
    }

    if (formData.phoneNumber.trim() && !isValidPhoneNumber(formData.phoneNumber)) {
      setError(PHONE_NUMBER_ERROR);
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // Create outbound lead
      const authHeaders = authService.getAuthHeaders();
      // Remove the default Content-Type and add the correct one for form data
      const { 'Content-Type': _, ...headersWithoutContentType } = authHeaders;

      const response = await fetch(`${getApiBaseUrl()}/outbound-leads/initiate/${selectedFile.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          ...headersWithoutContentType
        },
        body: new URLSearchParams({
          phoneNumber: formData.phoneNumber,
          storeId: String(selectedStore)
        })
      });

      if (response.ok) {
        const leadData = await response.json();
        setSuccess(`Successfully initiated conversation for lead ${leadData.id}`);

        // Notify parent component
        if (onLeadCreated) {
          onLeadCreated(leadData);
        }

        // Close modal after success
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        try {
          const errorData = JSON.parse(errorText);
          setError(errorData.message || errorData.error || 'Failed to initiate conversation');
        } catch {
          setError(`Failed to initiate conversation: ${response.status} ${response.statusText}`);
        }
      }
    } catch (err) {
      console.error('Network Error:', err);
      setError(`Error initiating conversation: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset state
    setSelectedFile(null);
    setFormData({ phoneNumber: '' });
    setError('');
    setSuccess('');
    setFileError('');
    onClose();
  };

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Start Outbound Conversation</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* File Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select File
            </label>
            {loadingFiles ? (
              <div className="flex items-center justify-center py-8 border border-gray-200 rounded-lg">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                <div className="text-gray-600">Loading available files...</div>
              </div>
            ) : fileError ? (
              <div className="border border-red-200 rounded-lg p-4">
                <div className="flex items-center text-red-700 mb-3">
                  <AlertCircle size={20} className="mr-2" />
                  <span>{fileError}</span>
                </div>
                <button
                  type="button"
                  onClick={handleRetryFetchFiles}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <RefreshCw size={16} className="mr-2" />
                  Retry
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3">
                {files.length === 0 ? (
                  <div className="text-gray-500 text-center py-4">No files available</div>
                ) : (
                  files.map((file) => (
                    <div
                      key={file.id}
                      onClick={() => handleFileSelect(file)}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedFile?.id === file.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium text-gray-900">{file.leadCategory.code} - {file.originalFileName}</div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

            {/* Phone Number */}
            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                id="phoneNumber"
                value={formData.phoneNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                placeholder="Enter phone number"
                aria-invalid={Boolean(formData.phoneNumber.trim() && !isValidPhoneNumber(formData.phoneNumber))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Error/Success Messages */}
            {storeWarning && (
              <div className="flex items-center p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertCircle size={20} className="text-yellow-600 mr-2" />
                <span className="text-yellow-700">{storeWarning}</span>
              </div>
            )}

            {error && (
              <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle size={20} className="text-red-600 mr-2" />
                <span className="text-red-700">{error}</span>
              </div>
            )}

            {success && (
              <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle size={20} className="text-green-600 mr-2" />
                <span className="text-green-700">{success}</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!selectedFile || submitting || !selectedStoreAssistant || !selectedStore}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    Starting...
                  </>
                ) : (
                  <>
                    <Play size={16} className="mr-2" />
                    Start Conversation
                  </>
                )}
              </button>
            </div>
          </form>
      </div>
    </div>
  );
};

export default OutboundLeadModal;
